import { useCallback, useEffect, useState } from "react"
import SelectedGovPanel from "../../api/Randoms/GovPoolModel/GovPerformCollection"
import AdditionalPanels from "./AdditionalPanels"
import MainPanels from "./MainPanels"
import { toast } from "react-toastify"
import { GovGetLabDeviceDrugs } from "../../api/Randoms/GovGetPanels"
import FormatError from "../../formatters/FormatError"
import { SampleType } from "../../api/Randoms/GovPoolModel/CollectionTypeResponse"
import { finalize } from "rxjs/operators"
import { Dictionary } from "../../api/GovPerformCollection/Dictionary"
import { PanelResult } from "../../api/GovPerformCollection/PanelResult"

export interface PanelsProps {
  labCode: string
  sampleTypeCode: string
  onUpdate: (panels: SelectedGovPanel[]) => void
  onLoading: (loading: boolean) => void
  deviceID: number
  accountID: number
}

const Panels = (props: PanelsProps) => {
  const { labCode, sampleTypeCode, onUpdate, onLoading, deviceID, accountID } = props

  const [loadingPanels, setLoadingPanels] = useState<boolean>(false)
  const [mainPanels, setMainPanels] = useState<Dictionary<SelectedGovPanel>>({})
  const [additionalPanels, setAdditionalPanels] = useState<Dictionary<SelectedGovPanel>>({})

  const isPOCTSample = () => ["QT", "QTS"].includes(sampleTypeCode)

  const isLabSample = useCallback(() => ["UR", "SLD"].includes(sampleTypeCode), [sampleTypeCode])

  const populatePanels = useCallback(
    (data: SampleType[]) => {
      return data.reduce<Dictionary<SelectedGovPanel>>((panels, sampleType) => {
        sampleType.Panels.map((panel) => {
          panels[panel.InternalCode || panel.ID] = {
            SelectedStatus: PanelResult.NOT_SELECTED,
            SendToLab: (isLabSample() && panel.InternalCode !== "") ? 1 : 0,   // Additional panels will have blank InternalCode
            SampleTypeID: sampleType.SampleTypeID,
            Code: panel.ID,
            Name: panel.Name,
            Label: panel.Name.split(" ")[0] || panel.InternalCode || "",
            ExternalID: panel.ExternalID || "",
            InternalCode: panel.InternalCode || "",
          }
        })
        return panels
      }, {})
    },
    [isLabSample]
  )

  useEffect(() => {
    setLoadingPanels(true)
    const subscription = GovGetLabDeviceDrugs(sampleTypeCode, labCode, deviceID, accountID)
      .pipe(finalize(() => setLoadingPanels(false)))
      .subscribe(
        (data) => {
          const mainPanels: Dictionary<SelectedGovPanel> = {}
          const additionalPanels: Dictionary<SelectedGovPanel> = {}
          Object.entries(populatePanels(data)).forEach(([code, panel]) => {
            ;(panel.InternalCode == "" ? additionalPanels : mainPanels)[code] = panel
          })
          setMainPanels(mainPanels)
          setAdditionalPanels(additionalPanels)
        },
        (error) => toast.error(FormatError(error))
      )
    return () => subscription.unsubscribe()
  }, [labCode, populatePanels])

  useEffect(() => {
    onLoading(loadingPanels)
  }, [loadingPanels, onLoading])

  useEffect(() => {
    onUpdate(Object.values({ ...mainPanels, ...additionalPanels }))
  }, [mainPanels, additionalPanels, onUpdate])

  return (
    <>
      {loadingPanels && <>Loading...</>}
      {isPOCTSample() && <MainPanels panels={mainPanels} loading={loadingPanels} onUpdate={setMainPanels} />}
      <AdditionalPanels panels={additionalPanels} onUpdate={setAdditionalPanels} />
    </>
  )
}

export default Panels
